/**
 * Tag-matching API client (Match Tags / Manage Tags) — talks to mttr/app_api.
 *
 * All endpoints are gated server-side to vendor + platform users; the token is
 * sent via the `access-token` header (gen5-pwa convention). Writes land in
 * _jcox.mttr.rfid_tag — never the WMS.
 */

import { ApiError, apiFetch } from "./apiClient";

export interface ItemHit {
  item_id: number;
  sku: string | null;
  name: string | null;
  category: string | null;
  manufacturer: string | null;
  barcode: string | null;
}

export interface TagMatch {
  epc: string;
  item_id: number;
  sku: string | null;
  name: string | null;
  quantity: number | null;
  source: "import" | "app";
  matched_by: string | null;
  matched_utc: string | null;
}

/** Result of GET /api/tags/{epc} — `matched` tells you if the EPC is assigned;
 *  the rest is present only when matched. */
export interface TagLookup extends Partial<TagMatch> {
  epc: string;
  matched: boolean;
}

export interface TagPage {
  total: number;
  page: number;
  page_size: number;
  tags: TagMatch[];
}

export class TagApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "TagApiError";
  }
}

async function request<T>(
  path: string,
  token: string,
  init?: RequestInit,
): Promise<T> {
  try {
    return await apiFetch<T>(path, token, init);
  } catch (e) {
    throw e instanceof ApiError ? new TagApiError(e.message, e.status) : e;
  }
}

/** Catalog search for the product picker (>= 2 chars; returns [] otherwise). */
export async function searchItems(
  query: string,
  token: string,
  limit = 25,
): Promise<ItemHit[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const body = await request<{ items: ItemHit[] }>(
    `/api/items?q=${encodeURIComponent(q)}&limit=${limit}`,
    token,
  );
  return body.items;
}

/** Current match for an EPC — `matched: false` when unassigned. */
export function lookupTag(epc: string, token: string): Promise<TagLookup> {
  return request<TagLookup>(`/api/tags/${encodeURIComponent(epc)}`, token);
}

/** Paginated tag-match list for Manage Tags. */
export function listTags(
  token: string,
  opts: { q?: string; itemId?: number; page?: number; pageSize?: number } = {},
): Promise<TagPage> {
  const p = new URLSearchParams();
  if (opts.q) p.set("q", opts.q);
  if (opts.itemId != null) p.set("item_id", String(opts.itemId));
  if (opts.page != null) p.set("page", String(opts.page));
  if (opts.pageSize != null) p.set("page_size", String(opts.pageSize));
  const qs = p.toString();
  return request<TagPage>(`/api/tags${qs ? `?${qs}` : ""}`, token);
}

/** Create/replace an EPC -> item match. */
export function matchTag(
  epc: string,
  itemId: number,
  quantity: number | null,
  token: string,
): Promise<TagLookup> {
  return request<TagLookup>(`/api/tags/${encodeURIComponent(epc)}/match`, token, {
    method: "POST",
    body: JSON.stringify({ item_id: itemId, quantity }),
  });
}

/** Soft-delete an EPC's match so it can be re-assigned. */
export function unmatchTag(epc: string, token: string): Promise<TagLookup> {
  return request<TagLookup>(
    `/api/tags/${encodeURIComponent(epc)}/unmatch`,
    token,
    { method: "POST", body: JSON.stringify({}) },
  );
}

// ---------- Tag lifecycle + retrieval (migration 087, 2026-06-16) ----------

export type TagStatus =
  | "PENDING"
  | "ON_HAND"
  | "CHECKED_OUT"
  | "DEPARTED"
  | "RETRIEVED"
  | "UNKNOWN";

export interface TagStatusRow {
  epc: string;
  item_id: number;
  sku: string | null;
  name: string | null;
  quantity: number | null;
  vendor_company_id: number | null;
  vendor_name: string | null;
  status: TagStatus;
  current_smartbox_id: number | null;
  current_smartbox_code: string | null;
  current_scu_name: string | null;
  last_event_utc: string | null;
  last_direction: string | null;
}

export interface TagStatusPage {
  total: number;
  page: number;
  page_size: number;
  tags: TagStatusRow[];
}

/** Paginated tag-lifecycle list. Server-side vendor scope; status is
 *  derived from the inventory ledger via mttr.vw_tag_status. */
export function listTagStatuses(
  token: string,
  opts: {
    q?: string; itemId?: number; status?: TagStatus | "";
    /** Location filter: the tag's current smartbox (md_smartbox.id). */
    smartboxId?: number;
    page?: number; pageSize?: number;
  } = {},
): Promise<TagStatusPage> {
  const p = new URLSearchParams();
  if (opts.q) p.set("q", opts.q);
  if (opts.itemId != null) p.set("item_id", String(opts.itemId));
  if (opts.status) p.set("status", opts.status);
  if (opts.smartboxId != null) p.set("smartbox_id", String(opts.smartboxId));
  if (opts.page != null) p.set("page", String(opts.page));
  if (opts.pageSize != null) p.set("page_size", String(opts.pageSize));
  const qs = p.toString();
  return request<TagStatusPage>(`/api/tags/status${qs ? `?${qs}` : ""}`, token);
}

export interface RetrieveResult {
  epc: string;
  ok: boolean;
  reason?: string | null;
  retrieval_id?: number | null;
  ledger_entry_id?: number | null;
}

/** Manually retrieve one ON_HAND tag. Refused (409) if not ON_HAND. */
export function retrieveTag(
  epc: string, token: string, reason?: string,
): Promise<RetrieveResult> {
  return request<RetrieveResult>(
    `/api/tags/${encodeURIComponent(epc)}/retrieve`,
    token,
    {
      method: "POST",
      body: JSON.stringify({ reason: reason ?? null }),
    },
  );
}

export interface BulkRetrieveResponse {
  requested: number;
  succeeded: number;
  results: RetrieveResult[];
}

/** Bulk retrieve. Each tag is its own atomic unit — a refusal on one
 *  doesn't block the rest. Max 500 epcs per call. */
export function bulkRetrieveTags(
  epcs: string[], token: string, reason?: string,
): Promise<BulkRetrieveResponse> {
  return request<BulkRetrieveResponse>(
    "/api/tags/retrieve/bulk", token,
    {
      method: "POST",
      body: JSON.stringify({ epcs, reason: reason ?? null }),
    },
  );
}

export type TagEventKind =
  | "MATCH"
  | "BASELINE"
  | "ARRIVAL"
  | "DEPARTURE"
  | "DEPARTURE_VOID"  // platform-only: a voided departure line (source DB)
  | "REVERSAL"
  | "ADJUSTMENT"
  | "RETRIEVAL";

export interface TagEvent {
  event_time_utc: string | null;
  kind: TagEventKind;
  actor: string | null;
  smartbox_id: number | null;
  smartbox_code: string | null;
  scu_name: string | null;
  qty: number;
  notes: string | null;
  related_departure_ticket_id: number | null;
  related_retrieval_id: number | null;
}

export interface TagHistory {
  epc: string;
  events: TagEvent[];
}

/** Full lifecycle timeline for one EPC. 404 when out of scope. */
export function fetchTagHistory(epc: string, token: string): Promise<TagHistory> {
  return request<TagHistory>(`/api/tags/${encodeURIComponent(epc)}/history`, token);
}

/** What one antenna made of one tag on one scan. Edge-sourced only — the
 *  cloud keeps a single best-RSSI antenna per tag and no signal detail. */
export interface AntennaRead {
  antenna: number;
  /** How many times this antenna read the tag during the scan. */
  reads: number;
  /** Best RSSI in dBm at this antenna. Closer to 0 is stronger. */
  best_rssi: number | null;
}

/** One reader's view of a tag on the current scan. */
export interface TagHeatReader {
  reader_id: number;
  /** Every port on this reader, so the dark ones can be drawn too. From the
   *  box's CONFIGURED antennas when edge-sourced — the cloud can only infer
   *  it from the highest port that read something, which shrinks a box whose
   *  top antenna was quiet. */
  port_count: number;
  /** MSB-first hex, antenna 1 = bit 31. Carries one bit on the cloud path;
   *  every antenna that saw the tag on the edge path. */
  detected_mask: string;
  detected: number[];
  /** Per-antenna reads + RSSI. Present on the edge path, absent on the cloud
   *  path — which is the difference between a heat map and a position. */
  reads?: AntennaRead[];
  /** Ports the BOX has switched off (`antenna.is_active = 0`). Edge path only.
   *  Drawn greyed: an antenna that is disabled and one that was listening and
   *  heard nothing look identical otherwise, and only the second is a fault.
   *  Measured: SB093 disables 9/11/15/17/21, SB071 disables 16, SB050 none. */
  disabled?: number[];
  /** The WHOLE reader is switched off (`reader.is_active = 0`). One fact, not
   *  32 — SB023 runs two 32-port readers with the second disabled, and without
   *  this its antennas each read as a fault. Edge path only. */
  reader_disabled?: boolean;
}

export interface TagHeatMapData {
  epc: string;
  /** False = deliberately nothing to draw (departed / checked out / retrieved). */
  on_hand: boolean;
  scu_name: string | null;
  smartbox_code: string | null;
  scan_end_utc: string | null;
  /** The box's recorded antenna layout (mttr.smartbox_antenna_layout). null =
   *  nobody has recorded it, and a 32-port box comes in more than one shelf
   *  configuration — so the drawing says it is unconfirmed rather than
   *  guessing a wall. */
  layout_key: string | null;
  readers: TagHeatReader[];
  /** Where these numbers came from. `edge` = read from the box itself, which
   *  is both fresher and per-antenna. `cloud` = the box could not be reached
   *  (or has never appeared in a scan), so this is `v2_mongo_scan_tag` — one
   *  antenna per tag, and for a box outside the ingest rotation it can be
   *  months old. Always shown to the operator rather than blended away. */
  source: "edge" | "cloud";
  /** The box's scanner version, edge path only. */
  scanner_version: string | null;
  /** Total tags on that scan, edge path only. */
  scan_total_tags: number | null;
}

/** Which antennas are seeing this tag right now. On-hand tags only — the
 *  server answers `on_hand: false` with no readers for anything else. */
export function fetchTagHeatMap(epc: string, token: string): Promise<TagHeatMapData> {
  return apiFetch<TagHeatMapData>(`/api/tags/${encodeURIComponent(epc)}/heatmap`, token);
}
